import * as React from 'react';
import type { TContactParam } from '@cian/newbuilding-chat-component';
import { useDispatch, useSelector } from 'react-redux';

import { requestAuthentication } from '../../actions/authentication';
import { closeBuilderChat, openBuilderChat } from '../../actions/builderChat';
import { getGaLabel } from '../../selectors/analytics';
import { getBuilders } from '../../selectors/newbuilding';
import { getUser } from '../../selectors/user';
import { TThunkDispatch } from '../../types/redux/thunk';
import { trackOpenChat, trackSendMessageClick } from '../../utils/analytics';

export function useBuilderChat(placeType: string) {
  const dispatch: TThunkDispatch = useDispatch();
  const user = useSelector(getUser);
  const gaLabel = useSelector(getGaLabel);
  const builders = useSelector(getBuilders);

  const openChatWithTracking = React.useCallback(
    (chatContact: TContactParam) => {
      const isBuilder = builders.some(builder => builder.realtyUserId === chatContact.realtyUserId);

      trackOpenChat({ label: gaLabel, placeType, sellerType: isBuilder ? 'builders' : 'agents' });
      dispatch(openBuilderChat(chatContact));
    },
    [builders, dispatch, gaLabel, placeType],
  );

  const openChat = React.useCallback(
    (chatContact: TContactParam) => {
      const isBuilder = builders.some(builder => builder.realtyUserId === chatContact.realtyUserId);

      trackSendMessageClick({ label: gaLabel, placeType, sellerType: isBuilder ? 'builders' : 'agents' });

      if (!user) {
        dispatch(
          requestAuthentication({
            onSuccess: () => () => openChatWithTracking(chatContact),
            onCloseNoAuth: closeBuilderChat,
          }),
        );

        return;
      }

      openChatWithTracking(chatContact);
    },
    [builders, gaLabel, placeType, user, openChatWithTracking, dispatch],
  );

  return { openChat };
}
